import axios from "axios";
import { authService } from "../services/auth/authService";
import { getEnvironmentConfig } from "../utils/detectEnv";
import { extractApiErrorMessage } from "../utils/apiError";
// Get baseURL from environment config
const envConfig = getEnvironmentConfig();
const baseURL = envConfig.apiBaseUrl;

console.log("Axios baseURL configured:", baseURL);

const api = axios.create({
  baseURL: baseURL,
});

// Shared state to prevent concurrent token refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

api.interceptors.request.use(
  (config) => {
    // Get access token from sessionStorage
    const accessToken = authService.getAccessToken();

    // Set X-nuid header
    config.headers["X-nuid"] = sessionStorage.getItem("userId") ?? "";

    // Set Authorization header with Bearer token
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
      console.log("Authorization header set with token");
    } else {
      config.headers["Authorization"] = "";
      console.log("Authorization header set to empty string (no token)");
    }
    try {
      const base = api.defaults.baseURL || "";
      const relative = String(config.url || "");
      const urlObj = new URL(relative, base);

      // Log original URL
      console.log("Request URL:", urlObj.toString());

      // Always append envlbl=drn (will become an additional query param if any already exist)
      if (envConfig.env == 'drn') {
        urlObj.searchParams.append('envlbl', 'drn');
      } else if (envConfig.env == 'dr') {
        urlObj.searchParams.append('envlbl', 'dr');
      }
      console.log('Modified Request URL (envlbl added):', urlObj.toString());

      // Build merged params for axios config.params
      let paramsObj: Record<string, any> = {};

      if (config.params instanceof URLSearchParams) {
        paramsObj = Object.fromEntries(config.params.entries());
      } else if (config.params && typeof config.params === "object") {
        paramsObj = { ...(config.params as Record<string, any>) };
      }

      // Merge params from the modified URL (these include envlbl plus any that were in the URL)
      const urlParams = Object.fromEntries(urlObj.searchParams.entries());
      paramsObj = { ...paramsObj, ...urlParams };

      config.params = paramsObj;

      // Strip any query string from config.url to avoid double-encoding of params by axios
      if (typeof config.url === "string") {
        const [pathOnly] = relative.split("?");
        config.url = pathOnly;
      }
    } catch (e) {
      console.warn("Unable to build request URL for logging", e);
    }

    return config;
  },
  (error) => {
    throw error;
  },
);

// Response interceptor to handle 401 errors (token expired)
api.interceptors.response.use(
  (response) => {
    // Treat API business failures the same as HTTP failures so every thunk
    // reaches its standard error path, including responses with HTTP 200.
    if (response.data?.success === false) {
      const error = new Error(
        extractApiErrorMessage({ response }, "Request could not be completed"),
      ) as Error & {
        code?: string;
        config?: typeof response.config;
        request?: typeof response.request;
        response?: typeof response;
      };
      error.name = "AxiosError";
      error.code = "ERR_BAD_RESPONSE";
      error.config = response.config;
      error.request = response.request;
      error.response = response;
      throw error;
    }
    return response;
  },
  async (error) => {
    if (error?.response) {
      error.message = extractApiErrorMessage(error, error.message);
    }

    const originalRequest = error.config;

    // ==================== NETWORK ERROR HANDLING ====================
    if (!error.response && error.code === "ERR_NETWORK") {
      console.error("Network Error:", {
        code: error.code,
        message: error.message,
        url: originalRequest?.url,
      });
      throw error;
    }

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string | null) => {
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(api(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;

      try {
        console.log("Token expired - attempting to refresh...");
        const refreshed = await authService.refreshAccessToken();

        if (refreshed) {
          // Get new token and retry original request
          const newToken = authService.getAccessToken();
          isRefreshing = false;
          onTokenRefreshed(newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Token refresh failed - redirect to login
          console.log("Token refresh returned false - redirecting to login");
          isRefreshing = false;
          onTokenRefreshed(null);
          authService.initiateLogin();
          throw error;
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        isRefreshing = false;
        onTokenRefreshed(null);
        // Redirect to login
        authService.initiateLogin();
        throw refreshError;
      }
    }
    throw error;
  },
);

export default api;
